import {
  Button,
  ButtonColor,
  FontWeight,
  formatPrice,
  Modal,
  ModalContent,
  type ModalHook,
  ModalSize,
  pluralize,
  Select,
  type SelectOption,
  Tag,
  TagColor,
  TagRounding,
  TagSize,
  useControlledModalState,
} from '@leland-dev/leland-ui-library';
import Image from 'next/image';
import React, { type FC, useCallback, useMemo, useState } from 'react';

import {
  GoalName,
  type OfferingPackagePrice,
  SkuTier,
} from '../../__generated-gql-types__/globalTypes';
import {
  type EstimatedSkuPrice,
  type PlanPrice,
  type SkuPrice,
} from '../../types/plan-price';
import {
  CoachingTimeType,
  offeringPackagePricesToPlanPrices,
} from '../../utils/package';
import SvgIcon, { Svg } from '../SvgIcon';

import {
  type SelectAPackageSizeModalOfferingPackageFragment,
  useSelectAPackageSizeModalFindSkuQuery,
} from './__generated-gql-types__/SelectAPackageSizeModal.generated';

export interface SelectAPackageSizeHookProps {
  offeringPackage: SelectAPackageSizeModalOfferingPackageFragment;
  onChoose: (tier: OfferingPackagePrice) => void;
  isLoading: boolean;
  skuTier?: Possible<SkuTier>;
  coachView?: boolean;
  isUpgrade?: boolean;
  upgradedFromPrice?: number;
}

interface SelectAPackageSizeProps extends SelectAPackageSizeHookProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const SelectAPackageSizeComponent: FC<SelectAPackageSizeProps> = ({
  coachView,
  isLoading,
  offeringPackage,
  onChoose,
  open,
  setOpen,
  skuTier,
  isUpgrade,
  upgradedFromPrice,
}) => {
  const tierOptions = useMemo(() => {
    return skuTier
      ? offeringPackage.prices?.filter((price) => {
          if (!price.tier) return false;
          return price.tier === skuTier;
        })
      : offeringPackage.prices;
  }, [offeringPackage.prices, skuTier]);

  const planPrices = useMemo(
    () => (tierOptions ? offeringPackagePricesToPlanPrices(tierOptions) : null),
    [tierOptions],
  );

  const [selectedTier, setSelectedTier] = useState<Nullable<string>>(null);
  const [selectedComprehensiveSku, setSelectedComprehensiveSku] =
    useState<Nullable<string>>(null);

  const onCheckoutClick = useCallback(() => {
    const skuToCheck =
      selectedTier === 'Comprehensive'
        ? selectedComprehensiveSku
        : selectedTier;
    const selection = offeringPackage.prices?.find(
      (p) => p.sku === skuToCheck && p.tier === skuTier,
    );

    if (selection) onChoose(selection);
  }, [
    offeringPackage.prices,
    onChoose,
    selectedComprehensiveSku,
    selectedTier,
    skuTier,
  ]);

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalContent size={ModalSize.LARGE} useFullSpace>
        <div className="grid size-full auto-cols-fr grid-cols-2 sm:overflow-hidden">
          <div className="col-span-1 hidden min-h-0 overflow-auto bg-select-a-tier-modal bg-cover bg-fixed bg-center bg-no-repeat p-10 lg:block">
            {offeringPackage.imageLink ? (
              <Image
                className="aspect-entity-image w-40 rounded-lg object-cover"
                src={offeringPackage.imageLink}
                alt={`${offeringPackage.name} image`}
                width={450}
                height={235}
              />
            ) : null}
            <h1 className="mt-6 text-2xl font-medium text-white">
              {offeringPackage.name}
            </h1>
            <div className="flex items-center justify-start space-x-2 text-lg font-normal text-white/80">
              {offeringPackage.category ? (
                <>
                  <p>{offeringPackage.category.name}</p>
                </>
              ) : null}
            </div>

            {offeringPackage.deliverables &&
            offeringPackage.deliverables.length > 0 ? (
              <div className="mt-12 flex flex-col items-start justify-start">
                <h3 className="text-sm font-medium uppercase text-leland-primary">
                  {isUpgrade
                    ? 'About upgrading packages'
                    : 'What you get from this package:'}
                </h3>
                {isUpgrade ? (
                  <p className="mb-12 text-lg text-white">
                    Upgrading package sizes is common for customers who realize
                    they need more coaching than they originally planned. When
                    you upgrade your package size, you pay the difference in
                    sizes.
                  </p>
                ) : (
                  offeringPackage.deliverables.map((deliverable, index) => (
                    <div
                      key={index}
                      className="mt-6 flex items-start justify-start space-x-3 first:mt-0"
                    >
                      <SvgIcon
                        svgType={Svg.CHECK_CIRCLE_GREEN}
                        className="mt-0.5 size-5 shrink-0 text-white"
                      />
                      <p className="text-lg font-normal leading-6 text-white">
                        {deliverable}
                      </p>
                    </div>
                  ))
                )}
              </div>
            ) : null}
          </div>
          <div className="col-span-2 flex min-h-0 flex-col sm:overflow-hidden lg:col-span-1">
            <h1 className="p-6 text-2xl font-medium text-leland-gray-dark">
              {coachView
                ? 'Available package sizes'
                : isUpgrade
                  ? 'Select an upgrade size'
                  : 'Select a package size'}
            </h1>
            {isUpgrade ? (
              <p className="px-6 pb-6 text-leland-gray-light">
                Only displaying sizes larger than your current package size.
              </p>
            ) : null}
            <div className="flex grow flex-col gap-4 overflow-scroll px-6 pb-6 pt-1">
              {planPrices?.map((tier) => (
                <Tier
                  coachView={coachView}
                  upgradedFromPrice={upgradedFromPrice}
                  key={tier.name}
                  goal={offeringPackage.category?.goal ?? GoalName.SCHOOL}
                  selectedSkuTier={skuTier ?? SkuTier.PRO}
                  selectedComprehensiveSku={selectedComprehensiveSku}
                  tier={tier}
                  selectedSku={selectedTier}
                  onSelect={(sku, compSku) => {
                    setSelectedTier(sku);
                    setSelectedComprehensiveSku(compSku);
                  }}
                />
              ))}
            </div>
            {coachView ? null : (
              <div className="flex justify-end border-t border-leland-gray-stroke p-4">
                <Button
                  label="Continue to checkout"
                  buttonColor={ButtonColor.PRIMARY}
                  disabled={
                    !selectedTier ||
                    isLoading ||
                    (selectedTier === 'Comprehensive' &&
                      !selectedComprehensiveSku)
                  }
                  type="button"
                  onClick={() => {
                    onCheckoutClick();
                    setOpen(false);
                  }}
                  id="select_a_package_size_modal--continue_to_checkout"
                />
              </div>
            )}
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

interface ComprehensivePriceProps {
  comprehensivePrice: number;
  hourlyPriceForMaxHours: number;
  upgradedFromPrice?: number;
}

const ComprehensivePrice: FC<ComprehensivePriceProps> = ({
  comprehensivePrice,
  hourlyPriceForMaxHours,
  upgradedFromPrice,
}) => {
  return (
    <div className="flex flex-row gap-1.5">
      <span className="text-leland-gray-light">
        {formatPrice(
          upgradedFromPrice
            ? comprehensivePrice - upgradedFromPrice
            : comprehensivePrice,
        )}
      </span>
      {upgradedFromPrice ? (
        <span>to upgrade</span>
      ) : hourlyPriceForMaxHours &&
        hourlyPriceForMaxHours > comprehensivePrice ? (
        <div className="flex flex-row items-center gap-1.5">
          <span className="text-leland-gray-extra-light line-through">
            {formatPrice(hourlyPriceForMaxHours)}
          </span>
          <div className="size-0.5 rounded-full bg-leland-gray-light" />
          <span className="font-medium text-leland-primary">
            Save {formatPrice(hourlyPriceForMaxHours - comprehensivePrice)}
          </span>
        </div>
      ) : null}
    </div>
  );
};

interface TierProps {
  coachView?: boolean;
  goal: GoalName;
  onSelect: (sku: string, comprehensiveTierSku: Nullable<string>) => void;
  selectedComprehensiveSku: Nullable<string>;
  selectedSku: Nullable<string>;
  selectedSkuTier: SkuTier;
  tier: PlanPrice;
  upgradedFromPrice?: number;
}

const Tier: FC<TierProps> = ({
  coachView,
  goal,
  onSelect,
  selectedComprehensiveSku,
  selectedSku,
  selectedSkuTier,
  tier,
  upgradedFromPrice,
}) => {
  const getComprehensiveSkuIfOnlyOneTier = (tier: PlanPrice) => {
    return tier?.tiers?.length === 1 ? (tier.tiers?.[0] as SkuPrice) : null;
  };

  const hasMultipleComprehensiveTiers =
    tier.tierType === CoachingTimeType.ESTIMATE && tier.tiers.length > 1;
  const [comprehensiveTierChoice, setComprehensiveTierChoice] = useState<
    Optional<SkuPrice>
  >(
    ((tier.tierType === CoachingTimeType.ESTIMATE &&
      tier.tiers.find((t) => t.sku === selectedComprehensiveSku)) ||
      getComprehensiveSkuIfOnlyOneTier(tier)) ??
      undefined,
  );

  const isSelected = tier.name === selectedSku;

  const { data: hourlyTierRate } = useSelectAPackageSizeModalFindSkuQuery({
    variables: {
      tier: selectedSkuTier,
    },
  });

  const tierPrice =
    tier.tierType === CoachingTimeType.EXACT
      ? tier.prices[0]
      : tier.tiers[0].prices[0];
  const tierOneHourUndiscounted = hourlyTierRate?.findSkuBySession?.price ?? 0;

  const tierUndiscounted =
    tierOneHourUndiscounted && tier.tierType === CoachingTimeType.EXACT
      ? tierOneHourUndiscounted * tier.hours
      : null;

  const comprehensiveTierOptions: Array<SelectOption<EstimatedSkuPrice>> =
    tier.tierType === CoachingTimeType.ESTIMATE
      ? tier.tiers.map((tier) => {
          return {
            label: `${tier.sku} (${tier.minHours} - ${tier.maxHours} hours)`,
            subTitle: coachView ? undefined : (
              <ComprehensivePrice
                comprehensivePrice={tier.prices[0]?.price ?? 0}
                hourlyPriceForMaxHours={
                  Math.round((tier.maxHours + tier.minHours) / 2) *
                  tierOneHourUndiscounted
                }
                upgradedFromPrice={upgradedFromPrice}
              />
            ),
            value: tier,
          };
        })
      : [];

  const tierHours =
    tier.tierType === CoachingTimeType.EXACT
      ? pluralize(tier.hours, 'hour')
      : `${Math.min(...tier.tiers.map((p) => p.minHours))} - ${Math.max(...tier.tiers.map((p) => p.maxHours))} hours`;

  return (
    <button
      className={`flex flex-col rounded-lg ${isSelected ? 'border-leland-gray-dark outline outline-1 outline-leland-gray-dark' : 'border-leland-gray-stroke'} border shadow-sm`}
      onClick={() => {
        if (tier.name === 'Comprehensive' && !hasMultipleComprehensiveTiers) {
          onSelect(tier.name, tier?.tiers?.[0]?.sku ?? null);
        } else {
          onSelect(tier.name, comprehensiveTierChoice?.sku ?? null);
        }
      }}
    >
      <div className="flex w-full gap-4 p-5">
        {coachView ? null : (
          <div className="flex h-7 items-center">
            <input
              className="form-radio rounded"
              type="radio"
              checked={isSelected}
              onChange={() => {
                onSelect(tier.name, comprehensiveTierChoice?.sku ?? null);
              }}
            />
          </div>
        )}
        <div className="flex w-full flex-col">
          <div className="flex items-center justify-between space-x-4">
            <div className="flex w-full flex-col items-start justify-center">
              <div className="flex w-full items-center">
                <h3
                  className="line-clamp-2 grow text-start text-lg font-medium text-leland-gray-dark"
                  title={tier.name}
                >
                  {tier.name === 'Comprehensive' &&
                  !hasMultipleComprehensiveTiers
                    ? (tier?.tiers?.[0]?.sku ?? null)
                    : tier.name}
                </h3>
                <span className="text-base text-leland-gray-light">
                  {tierHours}
                </span>
              </div>

              {coachView ? null : (
                <h4 className="flex items-center gap-1.5">
                  {tier.tierType === CoachingTimeType.EXACT ? (
                    <span className="text-leland-gray-light">
                      {formatPrice(tierPrice.price)}
                    </span>
                  ) : comprehensiveTierChoice ? (
                    <ComprehensivePrice
                      comprehensivePrice={
                        comprehensiveTierChoice.prices[0]?.price ?? 0
                      }
                      hourlyPriceForMaxHours={
                        (tier.tiers.find(
                          (t) => t.sku === comprehensiveTierChoice.sku,
                        )?.maxHours ?? 0) * tierOneHourUndiscounted
                      }
                      upgradedFromPrice={upgradedFromPrice}
                    />
                  ) : null}
                  {tierUndiscounted && tierUndiscounted > tierPrice.price ? (
                    <>
                      <span className="text-leland-gray-extra-light line-through">
                        {formatPrice(tierUndiscounted)}
                      </span>
                      <div className="size-0.5 rounded-full bg-leland-gray-light" />
                      <span className="font-medium text-leland-primary">
                        Save {formatPrice(tierUndiscounted - tierPrice.price)}
                      </span>
                    </>
                  ) : null}
                </h4>
              )}
            </div>
          </div>
          {tier.description ? (
            <>
              <hr className="my-4" />
              <div className="flex flex-col items-start justify-center">
                <p className="whitespace-pre-line break-words text-left text-base font-normal text-leland-gray-light">
                  {tier.description}
                </p>
              </div>
              {tier.freeLelandPlusPeriod?.amount ? (
                <div className="mt-3 flex items-start">
                  <Tag
                    text={`Includes Leland+ access for ${pluralize(tier.freeLelandPlusPeriod.amount, 'month')}`}
                    fontWeight={FontWeight.NORMAL}
                    tagColor={TagColor.GRAY}
                    size={TagSize.XS}
                    rounding={TagRounding.SM}
                  />
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
      {tier.tierType === CoachingTimeType.ESTIMATE &&
      isSelected &&
      hasMultipleComprehensiveTiers ? (
        <div className="flex w-full flex-col items-start rounded-b-lg bg-leland-gray-hover p-5">
          <Select<SkuPrice>
            label={
              goal === GoalName.SCHOOL
                ? 'How many schools are you applying to?'
                : goal === GoalName.CAREER
                  ? 'How many companies are you applying to?'
                  : 'How many tests are you taking?'
            }
            options={comprehensiveTierOptions}
            onValueChange={(compTier) => {
              setComprehensiveTierChoice(compTier);
              onSelect(tier.name, compTier.sku);
            }}
            value={comprehensiveTierChoice}
          />
        </div>
      ) : null}
    </button>
  );
};

export const useSelectAPackageSize: ModalHook<
  SelectAPackageSizeHookProps
> = () => {
  const [open, setOpen] = useControlledModalState();
  const SelectAPackageSize: FC<SelectAPackageSizeHookProps> = (props) =>
    open ? (
      <SelectAPackageSizeComponent {...props} open={open} setOpen={setOpen} />
    ) : null;
  return { open, setOpen, Modal: SelectAPackageSize };
};
